<script setup lang="ts">
import { InternetPhoneSchema } from '~/schemas/internet-phone';

const { data: page } = await useAsyncData(() => 
  queryCollection('InternetPhonePage').first()
);

useSeoMeta({
  title: page.value?.metaTitle,
  description: page.value?.metaDescription,
  ogUrl: page.value?.linkCanonical,
  ogTitle: page.value?.title,
  ogDescription: page.value?.description,
});

useHead({
  link: [
    { rel: "canonical", href: page.value?.linkCanonical },
  ],
  script: [
    {
      type: 'application/ld+json',
      textContent: JSON.stringify(InternetPhoneSchema)
    },
  ],
});

const modalsStore = useModalsStore();

function handleOnClickViewPrices() {
  const elem = document.getElementById('prices');
  elem?.scrollIntoView({ behavior: 'smooth' });
};

function handleOpenModal() {
  modalsStore.openModal('CTA');
};
</script>

<template>
  <template v-if="page">
    <SectionsBase class="relative">
      <svg 
        class="absolute w-full h-1/2 bottom-0 left-0 bg-linear-to-b from-transparent to-secondary-200 
          lg:rounded-br-[140px] lg:bottom-auto lg:top-0 lg:w-[35%] lg:h-full lg:bg-linear-to-bl lg:from-secondary-200
          lg:to-transparent"
      />
      <div class="container flex flex-col lg:flex-row items-center relative z-1">
        <div class="w-full lg:bg-[url('/img/new/backgrounds/mesh-01.webp')] bg-bottom bg-no-repeat">
          <h1 class="text-primary-600 highlight">{{ page.title }}</h1>
          <span class="h1"><span class="semicolon">؛</span> {{ page.subTitle }}</span>
          <p class="text-gray-500 text-body1-semibold mt-6 max-w-200 text-center lg:text-start">{{ page.description }}</p>
          <div class="w-full flex flex-col lg:flex-row items-center my-10 lg:my-15 gap-5">
            <Button
              :append-icon="{ name: 'solar:round-alt-arrow-left-bold' }"
              size="Large"
              pill
              @click="handleOpenModal"
              class="w-44.5 [&_.btn-slot-parent]:w-full [&_.btn-slot-parent]:justify-between!"
            >
              مشاوره رایگان
            </Button>
            <Button
              type="Outline"
              :append-icon="{ name: 'solar:round-alt-arrow-left-bold' }"
              size="Large"
              pill
              @click="handleOnClickViewPrices"
              class="w-44.5"
            >
              مشاهده تعرفه‌ها
            </Button>
          </div>
          <div class="desktop-only w-full lg:flex! flex-col my-15 gap-3">
            <div 
              v-for="item in page.items"
              :key="item"
              class="flex items-start gap-3"
            >
              <Icon name="solar:check-circle-bold" size="24" class="text-secondary-600" />
              <span class="text-gray-400 text-body1-semibold">{{ item }}</span>
            </div>
          </div>
        </div>
        <NuxtImg
          width="520"
          height="430"
          :src="page.img"
          :alt="page.title"
          sizes="200px md:350px lg:500px 2xl:520px"
          :preload="{ fetchPriority: 'high' }"
          fetchpriority="high"
          loading="eager"
          class="mx-auto"
        />
        <div 
          class="mobile-only w-full flex flex-col pb-10 mt-10 -mb-7.5 gap-3 bg-[url('/img/new/backgrounds/mesh-01.webp')]
            bg-bottom bg-no-repeat"
        >
          <div 
            v-for="item in page.items"
            :key="item"
            class="flex items-start gap-3"
          >
            <Icon name="solar:check-circle-bold" size="24" class="text-secondary-600" />
            <span class="text-gray-400 text-body1-semibold">{{ item }}</span>
          </div>
        </div>
      </div>
    </SectionsBase>

    <LazySectionsAboutInternetPhone
      :title="page.about.title"
      :desc="page.about.desc"
    />

    <LazySectionsLogos />

    <LazyVisibleTOC hydrate-on-visible :topics="page.topics" />

    <LazySectionsIntroInternetPhone
      :title="page.intro.title"
      :desc="page.intro.desc"
      :img="page.intro.img"
      :items="page.intro.items"
    />

    <LazyIdleSectionsFeaturesInternetPhone
      hydrate-on-idle
      :title="page.features.title"
      :desc="page.features.desc"
      :items="page.features.items"
    />

    <LazyIdleSectionsPrefixes
      hydrate-on-idle
      :title="page.prefixes.title"
      :desc="page.prefixes.desc"
    />

    <LazyVisibleSectionsPricesInternetPhone
      hydrate-on-visible
      :title="page.prices.title"
      :desc="page.prices.desc"
    />

    <LazyVisibleSectionsRelatedProductsPathways
      hydrate-on-visible
      :title="page.relatedProducts.title"
      :desc="page.relatedProducts.desc"
      :currentProduct="page.relatedProducts.currentProduct"
      :icon="page.relatedProducts.icon"
      :items="page.relatedProducts.items"
    />

    <LazyVisibleSectionsNavatelOverview
      hydrate-on-visible
      :title="page.navatelOverview.title"
      :subTitle="page.navatelOverview.subTitle"
      :desc="page.navatelOverview.desc"
    />

    <LazyVisibleSectionsSolutionsCarousel
      hydrate-on-visible
      :title="page.solutions.title"
      :desc="page.solutions.desc"
      :items="page.solutions.items"
    />

    <LazyVisibleSectionsWhyNavatel
      hydrate-on-visible
      :title="page.whyNavatel.title"
      :desc="page.whyNavatel.desc"
      :items="page.whyNavatel.items"
    />

    <LazyVisibleSectionsComments hydrate-on-visible />

    <SectionsFAQ
      :title="page.FAQ.title"
      :items="page.FAQ.items"
    />
    
    <LazyVisibleSectionsCTA 
      hydrate-on-visible
      :title="page.CTA.title" 
      :desc="page.CTA.desc"
    />
  </template>
</template>
